import React, { useState, useEffect, useCallback } from 'react'
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Tag,
  Tooltip,
  List,
  message
} from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import Hint from './common/Hint'
import dayjs from 'dayjs'
import { getRevisitReminderLogs } from '../api/revisitReminder'
import { formatTz } from '../utils/timezone'

const { RangePicker } = DatePicker

/**
 * 回訪提醒的發送紀錄。
 *
 * 店家要回答的是三個問題：發過幾次、發給誰、誰沒收到。
 * 所以上方摘要用「發送批次」而不是「收件人次」當主數字 ——
 * 店家講「發了三次」指的是按了三次發送，不是收到訊息的人有三個。
 */
const RevisitReminderLogs = ({ businessId, configs = [] }) => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState({
    batchCount: 0,
    successCount: 0,
    failedCount: 0,
    backfilledCount: 0
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    configId: undefined,
    trigger: undefined,
    result: undefined,
    range: null,
    keyword: ''
  })
  // 送出查詢用的關鍵字：打字時不要每個字都打一次 API
  const [appliedKeyword, setAppliedKeyword] = useState('')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  )

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const fetchLogs = useCallback(
    async (page = 1, pageSize = 20) => {
      if (!businessId) return
      setLoading(true)
      try {
        const params = {
          page,
          pageSize,
          configId: filters.configId,
          trigger: filters.trigger,
          result: filters.result,
          keyword: appliedKeyword || undefined,
          startDate: filters.range?.[0]
            ? filters.range[0].format('YYYY-MM-DD')
            : undefined,
          endDate: filters.range?.[1]
            ? filters.range[1].format('YYYY-MM-DD')
            : undefined
        }
        const res = await getRevisitReminderLogs(businessId, params)
        const data = res.data?.data || {}
        setRows(data.list || [])
        setSummary(
          data.summary || {
            batchCount: 0,
            successCount: 0,
            failedCount: 0,
            backfilledCount: 0
          }
        )
        setPagination(
          data.pagination || { current: page, pageSize, total: 0 }
        )
      } catch (error) {
        console.error('取得發送紀錄失敗:', error)
        message.error('取得發送紀錄失敗')
      } finally {
        setLoading(false)
      }
    },
    [businessId, filters, appliedKeyword]
  )

  useEffect(() => {
    fetchLogs(1, pagination.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, filters, appliedKeyword])

  const channelTag = (row) => {
    if (!row.success && row.channel === 'none') return <Tag>未送出</Tag>
    if (row.channel === 'line') return <Tag color='green'>LINE</Tag>
    if (row.channel === 'email') return <Tag color='blue'>Email</Tag>
    return <Tag>—</Tag>
  }

  const resultTag = (row) =>
    row.success ? (
      <Tag color='success'>成功</Tag>
    ) : (
      <Tooltip title={row.failReason || '發送失敗'}>
        <Tag color='error'>失敗</Tag>
      </Tooltip>
    )

  const columns = [
    {
      title: '發送時間',
      dataIndex: 'sentAt',
      width: 160,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—')
    },
    {
      title: '客人',
      dataIndex: 'customerName',
      width: 120,
      render: (v) => v || '（未留姓名）'
    },
    {
      title: '提醒設定',
      dataIndex: 'configName',
      width: 160,
      render: (v, row) => (
        <Space size={4} wrap>
          <span>{v || '（已刪除）'}</span>
          <Tag
            color={
              { revisit: 'blue', followup: 'purple', broadcast: 'gold' }[
                row.configType
              ] || 'default'
            }
          >
            {{ revisit: '回訪提醒', followup: '貼心提醒', broadcast: '節慶／公告' }[
              row.configType
            ] || row.configType}
          </Tag>
        </Space>
      )
    },
    {
      title: '管道',
      dataIndex: 'channel',
      width: 90,
      render: (_, row) => channelTag(row)
    },
    {
      title: '結果',
      dataIndex: 'success',
      width: 90,
      render: (_, row) => resultTag(row)
    },
    {
      title: '原因',
      dataIndex: 'failReason',
      ellipsis: true,
      render: (v) => v || '—'
    },
    {
      title: '發送方式',
      dataIndex: 'trigger',
      width: 150,
      render: (v, row) => {
        if (v === 'manual') {
          return (
            <span>手動{row.operatorName ? `（${row.operatorName}）` : ''}</span>
          )
        }
        if (v === 'unknown') {
          // 回填進來的舊資料當年沒記是哪一種，不猜
          return (
            <Tooltip title='這筆是從舊資料回填的，當時沒有記錄是排程發的還是手動發的'>
              <span style={{ color: '#999' }}>無法判斷</span>
            </Tooltip>
          )
        }
        return <span>自動排程</span>
      }
    },
    {
      title: '',
      dataIndex: 'backfilled',
      width: 70,
      render: (v) =>
        v ? (
          <Tooltip title='這筆是「發送紀錄」功能上線前的舊資料，由系統回填'>
            <Tag>回填</Tag>
          </Tooltip>
        ) : null
    }
  ]

  const filterBar = (
    <Space wrap style={{ marginBottom: 16 }}>
      <Select
        allowClear
        placeholder='全部提醒設定'
        style={{ minWidth: 180 }}
        value={filters.configId}
        onChange={(v) => setFilters((f) => ({ ...f, configId: v }))}
        options={configs.map((c) => ({ label: c.name, value: c._id }))}
      />
      <Select
        allowClear
        placeholder='全部發送方式'
        style={{ minWidth: 140 }}
        value={filters.trigger}
        onChange={(v) => setFilters((f) => ({ ...f, trigger: v }))}
        options={[
          { label: '自動排程', value: 'auto' },
          { label: '手動發送', value: 'manual' }
        ]}
      />
      <Select
        allowClear
        placeholder='全部結果'
        style={{ minWidth: 120 }}
        value={filters.result}
        onChange={(v) => setFilters((f) => ({ ...f, result: v }))}
        options={[
          { label: '成功', value: 'success' },
          { label: '失敗', value: 'failed' }
        ]}
      />
      <RangePicker
        value={filters.range}
        onChange={(v) => setFilters((f) => ({ ...f, range: v }))}
      />
      <Input
        allowClear
        placeholder='客人姓名'
        style={{ width: 160 }}
        value={filters.keyword}
        suffix={<SearchOutlined />}
        onChange={(e) =>
          setFilters((f) => ({ ...f, keyword: e.target.value }))
        }
        onPressEnter={() => setAppliedKeyword(filters.keyword)}
        onBlur={() => setAppliedKeyword(filters.keyword)}
      />
      <Button
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
      >
        刷新
      </Button>
    </Space>
  )

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={8}>
            <Statistic
              title='發送批次'
              value={summary.batchCount}
              suffix='次'
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title='成功送達'
              value={summary.successCount}
              suffix='人次'
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title='沒送到'
              value={summary.failedCount}
              suffix='人次'
              valueStyle={{
                color: summary.failedCount > 0 ? '#cf1322' : undefined
              }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
          「發送批次」是按下發送或排程執行的次數；同一次發給 30 個人算 1 次、30 人次。紀錄保留一年。
        </div>
        {summary.backfilledCount > 0 && (
          <div style={{ marginTop: 8 }}>
            <Hint tone='info' title='這段期間包含回填的舊資料'>
              其中 {summary.backfilledCount} 人次是「發送紀錄」上線前的舊紀錄，由系統從預約資料回填。
              舊系統只記錄發送成功的，所以那段期間的「沒送到」看起來會是 0 ——
              那是查不到，不代表當時每個人都收到了。這些紀錄也分不出是自動還是手動發的，
              批次數則是用「同一設定、同一分鐘」推估的。
            </Hint>
          </div>
        )}
      </Card>

      <Card>
        {filterBar}
        {isMobile ? (
          <List
            loading={loading}
            dataSource={rows}
            locale={{ emptyText: '這段期間沒有發送紀錄' }}
            renderItem={(row) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <Space wrap style={{ marginBottom: 4 }}>
                    <b>{row.customerName || '（未留姓名）'}</b>
                    {channelTag(row)}
                    {resultTag(row)}
                  </Space>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {formatTz(row.sentAt, 'YYYY-MM-DD HH:mm')}　
                    {row.configName || '（已刪除）'}　
                    {row.trigger === 'manual' ? '手動' : '自動'}
                  </div>
                  {!row.success && row.failReason && (
                    <div style={{ fontSize: 12, color: '#cf1322' }}>
                      {row.failReason}
                    </div>
                  )}
                </div>
              </List.Item>
            )}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (p, ps) => fetchLogs(p, ps)
            }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey='_id'
            loading={loading}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: '這段期間沒有發送紀錄' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆`,
              onChange: (p, ps) => fetchLogs(p, ps)
            }}
          />
        )}
      </Card>
    </div>
  )
}

export default RevisitReminderLogs
